
import {
  TrendingUp, AlertTriangle, ArrowUpRight, CircleCheck, XCircle,
  Info, Plus, Check,
} from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * Semantic states.
 *
 * Five states, each with an icon as well as a colour, so the meaning survives
 * greyscale. Two deliberate choices:
 *
 * 1. `positive` is blue, not emerald. Emerald means "there is an opportunity
 *    here". A card reading "working well" in the same green as "you are losing
 *    250 clicks a month" trains the eye to ignore the colour.
 * 2. Each state contributes exactly one accent: a 3px top rule. The previous
 *    card stacked a diagonal gradient, a 5px left border, a 1px all-round
 *    border, a resting shadow, a hover lift and a hover shadow at once.
 */
const CONFIG = {
  opportunity: {
    rule: "bg-emerald-600",
    icon: "text-emerald-700",
    badge: "opportunity",
    Icon: TrendingUp,
    label: "Opportunity",
    action: "text-emerald-700",
  },
  warning: {
    rule: "bg-amber-500",
    icon: "text-amber-700",
    badge: "attention",
    Icon: AlertTriangle,
    label: "Needs attention",
    action: "text-amber-700",
  },
  positive: {
    rule: "bg-blue-600",
    icon: "text-blue-600",
    badge: "positive",
    Icon: CircleCheck,
    label: "Working well",
    action: "text-blue-600",
  },
  error: {
    rule: "bg-red-600",
    icon: "text-red-600",
    badge: "error",
    Icon: XCircle,
    label: "Something failed",
    action: "text-red-600",
  },
  /**
   * The fallback. An unrecognised insight type used to render as an emerald
   * opportunity, so a backend change could silently dress a failure up as good
   * news. Neutral is the honest default.
   */
  info: {
    rule: "bg-line-strong",
    icon: "text-slate-600",
    badge: "info",
    Icon: Info,
    label: "Finding",
    action: "text-navy-950",
  },
};

/**
 * Labels whose value is a machine string (a URL, a query, an identifier) and
 * therefore belongs in a monospace face. Anything else is prose and stays in
 * the body font, because a long sentence set in 12px monospace is unreadable.
 */
const MONO_LABELS = /^(query|page|landing page|url|path|id)$/i;

/**
 * Splits `"Query: best sourdough near me"` into a label and a value.
 * Evidence without a leading label renders as a single unlabelled line.
 *
 * @param {string} raw
 * @returns {{ label: string|null, value: string, mono: boolean }}
 */
function parseEvidence(raw) {
  const at = raw.indexOf(":");
  if (at === -1 || at > 24) return { label: null, value: raw, mono: false };
  const label = raw.slice(0, at).trim();
  const value = raw.slice(at + 1).trim();
  if (!value) return { label: null, value: raw, mono: false };
  return { label, value, mono: MONO_LABELS.test(label) };
}

export default function InsightCard({ insight, index, onTrack, tracked, tracking }) {
  const cfg = CONFIG[insight.type] || CONFIG.info;
  const { Icon } = cfg;
  const evidence = typeof insight.evidence === "string" ? parseEvidence(insight.evidence) : null;

  return (
    <article
      id={`priority-${index}`}
      data-testid={`insight-card-${index}`}
      className={`ss-priority ${index === 0 ? "ss-current" : ""}`}
    >
      <div className="ss-priority-head">
        <span className="ss-priority-number">{String(index + 1).padStart(2, "0")}</span>
        <span className="ss-priority-state">{tracked ? "Tracking" : index === 0 ? "Start here" : index === 1 ? "Next" : "Then"}</span>
      </div>
      <h3 className="mt-4 font-heading text-h3 text-navy-950">{insight.headline}</h3>
      <p className="mt-2 flex-1 text-small text-slate-600">{insight.why_it_matters}</p>

      <div className="mt-5 border-t border-line pt-4">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
          Recommended action
        </span>
        <div className={`mt-1.5 flex items-start gap-2 font-semibold ${cfg.action}`}>
          <ArrowUpRight size={17} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span className="text-small leading-snug">{insight.action}</span>
        </div>
        {onTrack && (
          <Button
            type="button"
            variant={tracked ? "ghost" : "quiet"}
            size="sm"
            onClick={() => !tracked && onTrack(insight)}
            disabled={tracked || tracking}
            loading={tracking}
            data-testid={`track-fix-${index}`}
            className={`tap-safe mt-4 ${tracked ? "cursor-default bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : ""}`}
          >
            {!tracking && (tracked ? <Check size={15} /> : <Plus size={15} />)}
            {tracked ? "Tracking this fix" : "Track this fix"}
          </Button>
        )}
      </div>
      <p className="ss-source-badge">{insight.source || "Source not specified in finding"}</p>
      <details className="ss-evidence">
        <summary>Show evidence</summary>
        <p className="ss-finding-type"><Icon size={15} aria-hidden="true" />{cfg.label}</p>
        {evidence ? <><strong>{evidence.label}</strong><p>{evidence.value}</p></> : <p>No detailed evidence was returned for this finding.</p>}
        <p>Review the evidence before changing your website. Tracking records a fix here, without editing your site.</p>
      </details>
    </article>
  );
}

export { CONFIG as INSIGHT_STATES };
